import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Helmet } from "react-helmet"
import { createServiceRequest } from "../../redux/apiCalls"
import { publicRequest } from "../../requestMethods"
import { useDispatch, useSelector } from "react-redux"
import { Publish } from "@mui/icons-material"
import {
   getStorage,
   ref,
   uploadBytesResumable,
   getDownloadURL,
} from "firebase/storage"
import app from "../../firebase"
// Components
import Navbar from "../../components/Navbar/Navbar"
import Footer from "../../components/Footer/Footer"
import Button from "../../components/Button/Button"
// Styles
import {
   Container,
   Header,
   Title,
   Wrapper,
   FormItem,
   Form,
   StyledInputs,
   StyledLabel,
   StyledInput,
   StyledTextArea,
   StyledButton,
   Message,
   Upload,
   UploadIcon,
   UpdateImage,
   CheckboxGroup,
   RadioLabel,
   RadioInput,
   FilteredList,
   FilteredValue,
} from "./NewServiceRequest.styles"

const NewServiceRequest = () => {
   const { t } = useTranslation()
   const dispatch = useDispatch()
   const navigate = useNavigate()
   const [message, setMessage] = useState("")
   const [serviceRequest, setServiceRequest] = useState({})
   const [products, setProducts] = useState([])
   const [filteredProducts, setFilteredProducts] = useState([])
   const { currentUser: user } = useSelector((state) => state.user)
   const now = new Date()
   const timestamp =
      now.getFullYear().toString() +
      String(now.getMonth() + 1).padStart(2, "0") +
      String(now.getDate()).padStart(2, "0") +
      String(now.getHours()).padStart(2, "0") +
      String(now.getMinutes()).padStart(2, "0") +
      String(now.getSeconds()).padStart(2, "0")

   const sparePartTranslations = {
      full: t("serviceRequests.value.sparePart.s1"),
      temples: t("serviceRequests.value.sparePart.s2"),
      left: t("serviceRequests.value.sparePart.s3"),
      right: t("serviceRequests.value.sparePart.s4"),
      endtips: t("serviceRequests.value.sparePart.s5"),
      nosepads: t("serviceRequests.value.sparePart.s6"),
   }

   const handleChange = (e) => {
      const { name, value } = e.target

      setServiceRequest((prev) => ({
         ...prev,
         [name]: value,
      }))

      if (name === "productRef") {
         if (value.length >= 2) {
            const matches = products.filter((p) =>
               p.toLowerCase().includes(value.toLowerCase()),
            )
            setFilteredProducts(matches)
         } else {
            setFilteredProducts([])
         }
      }
   }

   const handleSubmit = async (e) => {
      e.preventDefault()
      const res = await createServiceRequest(
         serviceRequest,
         dispatch,
         user?.accessToken,
      )
      if (res === "success") {
         setMessage(t("serviceRequestPage.message.m1"))
      } else {
         setMessage(JSON.stringify(res))
      }
      setTimeout(() => {
         navigate("/account")
      }, 4000)
   }

   const handleSelect = (e) => {
      setServiceRequest((prev) => {
         return { ...prev, sparePart: e.target.value }
      })
   }

   const handleImages = (e) => {
      e.preventDefault()
      const storage = getStorage(app)
      const files = Array.from(e.target.files)

      files.forEach((file, index) => {
         const fileName = `${timestamp}_${user._id}_${index}`
         const storageRef = ref(storage, `Service/${fileName}`)
         const uploadTask = uploadBytesResumable(storageRef, file)

         uploadTask.on(
            "state_changed",
            null,
            (error) => console.log(error),
            () => {
               getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                  setServiceRequest((prev) => ({
                     ...prev,
                     images: [...(prev.images || []), downloadURL],
                  }))
               })
            },
         )
      })
   }

   const deleteImages = (e) => {
      e.preventDefault()
      setServiceRequest({ ...serviceRequest, images: [] })
   }

   useEffect(() => {
      const today = new Date().toISOString().split("T")[0]
      setServiceRequest({
         userId: user._id,
         sparePart: "full",
         requestDate: today,
      })
      const getProducts = async () => {
         try {
            const res = await publicRequest.get("products?category=optical")
            const products = res.data
            const variationsCodes = products
               .flatMap((product) =>
                  product.variations.map((variation) => variation.code),
               )
               .sort((a, b) => a.localeCompare(b))
            setProducts(variationsCodes)
         } catch (err) {
            setProducts(err)
         }
      }
      getProducts()
   }, [user])

   return (
      <>
         <Helmet>
            <title>{t("serviceRequestPage.meta.meta_title")}</title>
            <meta name="robots" content="noindex, nofollow" />
         </Helmet>
         <Container>
            <Navbar bgImageColor="light" />
            <Wrapper>
               <Header>
                  <Title>{t("serviceRequestPage.title.t2")}</Title>
                  <Form>
                     <StyledInputs>
                        <FormItem>
                           <StyledLabel>
                              {t("serviceRequestPage.label.l1")}
                           </StyledLabel>
                           <StyledInput
                              type="date"
                              name="requestDate"
                              value={serviceRequest.requestDate || ""}
                              onChange={handleChange}
                           />
                        </FormItem>
                        <FormItem>
                           <StyledLabel>
                              {t("serviceRequestPage.label.l2")}
                           </StyledLabel>
                           <StyledInput
                              type="text"
                              name="productRef"
                              autoComplete="off"
                              value={serviceRequest.productRef || ""}
                              onChange={handleChange}
                           />
                           {filteredProducts.length > 0 && (
                              <FilteredList>
                                 {filteredProducts.map((code) => (
                                    <FilteredValue
                                       key={code}
                                       style={{
                                          padding: "6px 8px",
                                          cursor: "pointer",
                                       }}
                                       onClick={() => {
                                          setServiceRequest((prev) => ({
                                             ...prev,
                                             productRef: code,
                                          }))
                                          setFilteredProducts([])
                                       }}
                                    >
                                       {code}
                                    </FilteredValue>
                                 ))}
                              </FilteredList>
                           )}
                        </FormItem>
                        <FormItem>
                           <StyledLabel>
                              {t("serviceRequestPage.label.l3")}
                           </StyledLabel>
                           <CheckboxGroup>
                              <RadioLabel>
                                 <RadioInput
                                    type="radio"
                                    value="full"
                                    onChange={handleSelect}
                                    checked={
                                       serviceRequest.sparePart === "full"
                                    }
                                 />
                                 {sparePartTranslations["full"]}
                              </RadioLabel>
                              <RadioLabel>
                                 <RadioInput
                                    type="radio"
                                    value="temples"
                                    onChange={handleSelect}
                                    checked={
                                       serviceRequest.sparePart === "temples"
                                    }
                                 />
                                 {sparePartTranslations["temples"]}
                              </RadioLabel>
                              <RadioLabel>
                                 <RadioInput
                                    type="radio"
                                    value="left"
                                    onChange={handleSelect}
                                    checked={
                                       serviceRequest.sparePart === "left"
                                    }
                                 />
                                 {sparePartTranslations["left"]}
                              </RadioLabel>
                              <RadioLabel>
                                 <RadioInput
                                    type="radio"
                                    value="right"
                                    onChange={handleSelect}
                                    checked={
                                       serviceRequest.sparePart === "right"
                                    }
                                 />
                                 {sparePartTranslations["right"]}
                              </RadioLabel>
                              <RadioLabel>
                                 <RadioInput
                                    type="radio"
                                    value="endtips"
                                    onChange={handleSelect}
                                    checked={
                                       serviceRequest.sparePart === "endtips"
                                    }
                                 />
                                 {sparePartTranslations["endtips"]}
                              </RadioLabel>
                              <RadioLabel>
                                 <RadioInput
                                    type="radio"
                                    value="nosepads"
                                    onChange={handleSelect}
                                    checked={
                                       serviceRequest.sparePart === "nosepads"
                                    }
                                 />
                                 {sparePartTranslations["nosepads"]}
                              </RadioLabel>
                           </CheckboxGroup>
                        </FormItem>
                        <FormItem>
                           <StyledLabel>
                              {t("serviceRequestPage.label.l4")}
                           </StyledLabel>
                           <StyledTextArea
                              name="comments"
                              onChange={handleChange}
                           />
                        </FormItem>
                        <FormItem>
                           <StyledLabel>
                              {t("serviceRequestPage.label.l10")}
                           </StyledLabel>
                           <Upload>
                              {serviceRequest.images?.map((img, index) => (
                                 <UpdateImage
                                    key={img}
                                    src={serviceRequest.images[index]}
                                 />
                              ))}
                              <StyledLabel htmlFor="imagesURL">
                                 <UploadIcon>
                                    <Publish />
                                 </UploadIcon>
                              </StyledLabel>
                              <StyledInput
                                 type="file"
                                 id="imagesURL"
                                 name="imagesURL"
                                 style={{ display: "none" }}
                                 multiple
                                 onChange={(e) => handleImages(e)}
                              />
                           </Upload>
                           <StyledButton onClick={(e) => deleteImages(e)}>
                              {t("serviceRequestPage.button.b2")}
                           </StyledButton>
                        </FormItem>
                     </StyledInputs>
                     <Button
                        callback={(e) => handleSubmit(e)}
                        text={t("serviceRequestPage.button.b1")}
                        submit
                        disabled={
                           !serviceRequest.requestDate ||
                           !serviceRequest.productRef ||
                           !serviceRequest.comments ||
                           message
                        }
                     />
                  </Form>
                  <Message>{message}</Message>
               </Header>
            </Wrapper>
            <Footer />
         </Container>
      </>
   )
}

export default NewServiceRequest
